// Gamification System — adapted from VaakSakhi
// XP, Levels, Streaks, Daily Goals, Achievements

const LEVEL_THRESHOLDS = [0, 100, 300, 600, 1000, 1500, 2500, 4000, 6000, 9000, 15000];
const LEVEL_NAMES = ["Newbie", "Starter", "Learner", "Explorer", "Builder", "Speaker", "Confident", "Fluent", "Advanced", "Master", "Legend"];

const ACHIEVEMENTS = [
  // ── Lessons ──
  { id: "first_lesson", title: "First Step", desc: "Complete your first lesson", icon: "🎯", category: "Lessons", tier: "bronze", check: (s) => s.lessonsCompleted >= 1 },
  { id: "five_lessons", title: "Getting Serious", desc: "Complete 5 lessons", icon: "📚", category: "Lessons", tier: "bronze", check: (s) => s.lessonsCompleted >= 5 },
  { id: "ten_lessons", title: "Dedicated Learner", desc: "Complete 10 lessons", icon: "🔥", category: "Lessons", tier: "silver", check: (s) => s.lessonsCompleted >= 10 },
  { id: "twenty_lessons", title: "Halfway Hero", desc: "Complete 20 lessons", icon: "⚡", category: "Lessons", tier: "silver", check: (s) => s.lessonsCompleted >= 20 },
  { id: "all_lessons", title: "Course Master", desc: "Complete all 60 lessons", icon: "👑", category: "Lessons", tier: "gold", check: (s) => s.lessonsCompleted >= 60 },

  // ── Tests ──
  { id: "first_test", title: "Test Taker", desc: "Pass your first test", icon: "✅", category: "Tests", tier: "bronze", check: (s) => s.testsPassed >= 1 },
  { id: "perfect_score", title: "Perfect Score", desc: "Get 100% on a test", icon: "💯", category: "Tests", tier: "silver", check: (s) => s.perfectTests >= 1 },
  { id: "perfect_5", title: "Perfectionist", desc: "Get 100% on 5 tests", icon: "🌟", category: "Tests", tier: "gold", check: (s) => s.perfectTests >= 5 },

  // ── Streak (general practice) ──
  { id: "streak_3", title: "3-Day Streak", desc: "Practice 3 days in a row", icon: "🔥", category: "Streak", tier: "bronze", check: (s) => s.streak >= 3 },
  { id: "streak_7", title: "Week Warrior", desc: "7-day practice streak", icon: "⭐", category: "Streak", tier: "silver", check: (s) => s.streak >= 7 },
  { id: "streak_30", title: "Monthly Master", desc: "30-day practice streak", icon: "🏆", category: "Streak", tier: "gold", check: (s) => s.streak >= 30 },
  { id: "streak_100", title: "Centurion", desc: "100-day practice streak", icon: "💎", category: "Streak", tier: "diamond", check: (s) => s.streak >= 100 },

  // ── Perfect days (BOTH halves done) — uses dailyPlan ──
  { id: "perfect_day_1", title: "First Perfect Day", desc: "Complete BOTH morning written + evening oral in one day", icon: "🌅", category: "Perfect Days", tier: "bronze", check: (s) => (s.perfectDays || 0) >= 1 },
  { id: "perfect_week", title: "Perfect Week", desc: "Complete both halves every day for 7 days", icon: "🗓️", category: "Perfect Days", tier: "silver", check: (s) => (s.perfectDayStreak || 0) >= 7 },
  { id: "perfect_month", title: "Perfect Month", desc: "30 perfect days in a row", icon: "🎖️", category: "Perfect Days", tier: "gold", check: (s) => (s.perfectDayStreak || 0) >= 30 },

  // ── XP ──
  { id: "xp_100", title: "First 100 XP", desc: "Earn your first 100 XP", icon: "✨", category: "XP", tier: "bronze", check: (s) => s.totalXp >= 100 },
  { id: "xp_500", title: "XP Hunter", desc: "Earn 500 XP total", icon: "💎", category: "XP", tier: "silver", check: (s) => s.totalXp >= 500 },
  { id: "xp_2000", title: "XP Champion", desc: "Earn 2000 XP total", icon: "🏅", category: "XP", tier: "gold", check: (s) => s.totalXp >= 2000 },
  { id: "xp_10000", title: "XP Legend", desc: "Earn 10,000 XP total", icon: "🦄", category: "XP", tier: "diamond", check: (s) => s.totalXp >= 10000 },

  // ── Vocabulary ──
  { id: "vocab_20", title: "Word Collector", desc: "Learn 20 vocabulary words", icon: "📝", category: "Vocabulary", tier: "bronze", check: (s) => s.wordsLearned >= 20 },
  { id: "vocab_100", title: "Word Wizard", desc: "Learn 100 vocabulary words", icon: "📖", category: "Vocabulary", tier: "silver", check: (s) => s.wordsLearned >= 100 },

  // ── Speaking & Oral ──
  { id: "speaking", title: "Voice Activated", desc: "Complete a speaking practice", icon: "🎤", category: "Speaking", tier: "bronze", check: (s) => s.speakingSessions >= 1 },
  { id: "speaking_10", title: "Microphone Master", desc: "Complete 10 oral practice sessions", icon: "🗣️", category: "Speaking", tier: "silver", check: (s) => s.speakingSessions >= 10 },
  { id: "conversations_3", title: "Conversationalist", desc: "Complete 3 conversations", icon: "💬", category: "Speaking", tier: "bronze", check: (s) => s.conversationsCompleted >= 3 },
  { id: "conversations_25", title: "Chat Champion", desc: "Complete 25 conversations", icon: "🎙️", category: "Speaking", tier: "silver", check: (s) => s.conversationsCompleted >= 25 },

  // ── Games ──
  { id: "first_game", title: "Game On!", desc: "Play your first mini-game", icon: "🎮", category: "Games", tier: "bronze", check: (s) => (s.gamesPlayed || 0) >= 1 },
  { id: "game_5", title: "Game Lover", desc: "Play 5 different games", icon: "🕹️", category: "Games", tier: "silver", check: (s) => (s.uniqueGamesPlayed || []).length >= 5 },
  { id: "game_all", title: "Game Master", desc: "Play all 10 mini-games", icon: "🏆", category: "Games", tier: "gold", check: (s) => (s.uniqueGamesPlayed || []).length >= 10 },
  { id: "game_score_50", title: "Combo King", desc: "Score 50+ in a single game", icon: "👑", category: "Games", tier: "silver", check: (s) => (s.bestGameScore || 0) >= 50 },
];

function getStoredStats() {
  try {
    const data = localStorage.getItem("gamification");
    if (data) return JSON.parse(data);
  } catch {}
  return {
    totalXp: 0,
    streak: 0,
    longestStreak: 0,
    lastPracticeDate: null,
    dailyMinutes: 0,
    dailyGoal: 15,
    dailyXp: 0,
    lessonsCompleted: 0,
    testsPassed: 0,
    perfectTests: 0,
    wordsLearned: 0,
    speakingSessions: 0,
    conversationsCompleted: 0,
    unlockedAchievements: [],
    weeklyActivity: [0, 0, 0, 0, 0, 0, 0], // Mon-Sun minutes
    errorTypes: {},
    challengeDay: 0,
    challengeStartDate: null,
    // Hearts & Gems
    hearts: 5,
    maxHearts: 5,
    heartsLastRefill: null,
    gems: 50,
    streakFreezes: 0,
  };
}

function saveStats(stats) {
  localStorage.setItem("gamification", JSON.stringify(stats));
}

export function getStats() {
  const stats = getStoredStats();
  // Check if streak is still valid
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();
  if (stats.lastPracticeDate && stats.lastPracticeDate !== today && stats.lastPracticeDate !== yesterday) {
    // Auto-use a streak freeze if user has any — preserves the streak
    if ((stats.streakFreezes || 0) > 0) {
      stats.streakFreezes -= 1;
      stats.lastPracticeDate = yesterday; // pretend yesterday was the last practice
      stats.lastFreezeUsedDate = today;
      saveStats(stats);
      // Notify user via global event so toast can pop
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("streakFreeze:used", { detail: { remaining: stats.streakFreezes, streak: stats.streak } }));
      }
    } else {
      stats.streak = 0;
      saveStats(stats);
    }
  }
  return stats;
}

export function getLevel(xp) {
  let level = 0;
  for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
    if (xp >= LEVEL_THRESHOLDS[i]) { level = i; break; }
  }
  const currentThreshold = LEVEL_THRESHOLDS[level];
  const nextThreshold = LEVEL_THRESHOLDS[level + 1] || LEVEL_THRESHOLDS[level] + 5000;
  const progress = ((xp - currentThreshold) / (nextThreshold - currentThreshold)) * 100;
  return { level, name: LEVEL_NAMES[level], xp, nextXp: nextThreshold, progress: Math.min(progress, 100) };
}

export function earnXP(amount, source) {
  const stats = getStoredStats();
  const today = new Date().toDateString();
  const dayOfWeek = new Date().getDay(); // 0=Sun, 1=Mon...
  const weekIdx = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to Mon=0

  // Reset daily stats if new day
  if (stats.lastPracticeDate !== today) {
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    if (stats.lastPracticeDate === yesterday) {
      stats.streak += 1;
    } else if (stats.lastPracticeDate !== today) {
      stats.streak = 1;
    }
    stats.dailyMinutes = 0;
    stats.dailyXp = 0;
    // Reset weekly on Monday
    if (dayOfWeek === 1) stats.weeklyActivity = [0, 0, 0, 0, 0, 0, 0];
  }

  stats.totalXp += amount;
  stats.dailyXp += amount;
  // Earn gems (1 gem per 10 XP)
  stats.gems = (stats.gems || 50) + Math.floor(amount / 10);
  stats.dailyMinutes += 5; // Approximate 5 min per activity
  stats.lastPracticeDate = today;
  stats.weeklyActivity[weekIdx] = (stats.weeklyActivity[weekIdx] || 0) + 5;
  if (stats.streak > stats.longestStreak) stats.longestStreak = stats.streak;

  // Track source
  if (source === "lesson") stats.lessonsCompleted = (stats.lessonsCompleted || 0) + 1;
  if (source === "test") stats.testsPassed = (stats.testsPassed || 0) + 1;
  if (source === "perfect_test") { stats.perfectTests = (stats.perfectTests || 0) + 1; stats.testsPassed += 1; }
  if (source === "speaking") stats.speakingSessions = (stats.speakingSessions || 0) + 1;
  if (source === "conversation") stats.conversationsCompleted = (stats.conversationsCompleted || 0) + 1;
  if (source === "vocabulary") stats.wordsLearned = (stats.wordsLearned || 0) + 1;
  if (source === "game") {
    stats.gamesPlayed = (stats.gamesPlayed || 0) + 1;
    if (amount > (stats.bestGameScore || 0)) stats.bestGameScore = amount;
  }

  // Check achievements
  const newAchievements = [];
  ACHIEVEMENTS.forEach((a) => {
    if (!stats.unlockedAchievements.includes(a.id) && a.check(stats)) {
      stats.unlockedAchievements.push(a.id);
      newAchievements.push(a);
    }
  });

  saveStats(stats);
  // Fire toast events for any newly unlocked achievements
  if (typeof window !== "undefined" && newAchievements.length > 0) {
    newAchievements.forEach((a) => {
      window.dispatchEvent(new CustomEvent("achievement:unlocked", { detail: a }));
    });
  }
  return { stats, newAchievements, levelInfo: getLevel(stats.totalXp) };
}

// Track which mini-game was played (for the "all 10 games" badge)
export function recordGamePlay(gameId, score = 0) {
  const stats = getStoredStats();
  stats.uniqueGamesPlayed = stats.uniqueGamesPlayed || [];
  if (!stats.uniqueGamesPlayed.includes(gameId)) stats.uniqueGamesPlayed.push(gameId);
  stats.gamesPlayed = (stats.gamesPlayed || 0) + 1;
  if (score > (stats.bestGameScore || 0)) stats.bestGameScore = score;
  saveStats(stats);
  checkAchievementsAndNotify();
}

// Mark today as a "perfect day" (called by dailyPlan.js when both halves done)
export function recordPerfectDay() {
  const stats = getStoredStats();
  const today = new Date().toDateString();
  if (stats.lastPerfectDay === today) return; // already counted today
  const yesterday = new Date(Date.now() - 86400000).toDateString();
  if (stats.lastPerfectDay === yesterday) {
    stats.perfectDayStreak = (stats.perfectDayStreak || 0) + 1;
  } else {
    stats.perfectDayStreak = 1;
  }
  stats.perfectDays = (stats.perfectDays || 0) + 1;
  stats.lastPerfectDay = today;
  if ((stats.perfectDayStreak || 0) > (stats.longestPerfectDayStreak || 0)) {
    stats.longestPerfectDayStreak = stats.perfectDayStreak;
  }
  saveStats(stats);
  checkAchievementsAndNotify();
}

// Run achievement checks + emit unlock events without granting XP
export function checkAchievementsAndNotify() {
  const stats = getStoredStats();
  stats.unlockedAchievements = stats.unlockedAchievements || [];
  const newOnes = [];
  ACHIEVEMENTS.forEach((a) => {
    if (!stats.unlockedAchievements.includes(a.id) && a.check(stats)) {
      stats.unlockedAchievements.push(a.id);
      newOnes.push(a);
    }
  });
  if (newOnes.length) {
    saveStats(stats);
    if (typeof window !== "undefined") {
      newOnes.forEach((a) => window.dispatchEvent(new CustomEvent("achievement:unlocked", { detail: a })));
    }
  }
  return newOnes;
}

// Read all achievements with locked/unlocked status
export function getAllAchievementsWithStatus() {
  const stats = getStoredStats();
  const unlocked = stats.unlockedAchievements || [];
  return ACHIEVEMENTS.map((a) => ({ ...a, unlocked: unlocked.includes(a.id) }));
}

export function trackError(errorType) {
  const stats = getStoredStats();
  stats.errorTypes[errorType] = (stats.errorTypes[errorType] || 0) + 1;
  saveStats(stats);
}

export function updateDailyGoal(minutes) {
  const stats = getStoredStats();
  stats.dailyGoal = minutes;
  saveStats(stats);
}

// 7-Day Challenge
export function startChallenge() {
  const stats = getStoredStats();
  stats.challengeDay = 1;
  stats.challengeStartDate = new Date().toDateString();
  saveStats(stats);
}

export function getChallengeStatus() {
  const stats = getStoredStats();
  if (!stats.challengeStartDate) return null;
  return { day: stats.challengeDay, startDate: stats.challengeStartDate };
}

export function advanceChallenge() {
  const stats = getStoredStats();
  if (stats.challengeDay < 7) stats.challengeDay += 1;
  saveStats(stats);
  return stats.challengeDay;
}

// Hearts System
export function getHearts() {
  const stats = getStoredStats();
  // Auto-refill hearts every 4 hours
  if (stats.heartsLastRefill) {
    const hoursSince = (Date.now() - new Date(stats.heartsLastRefill).getTime()) / 3600000;
    const refill = Math.floor(hoursSince / 4);
    if (refill > 0 && stats.hearts < stats.maxHearts) {
      stats.hearts = Math.min(stats.maxHearts, stats.hearts + refill);
      stats.heartsLastRefill = new Date().toISOString();
      saveStats(stats);
    }
  }
  return stats.hearts;
}

export function loseHeart() {
  const stats = getStoredStats();
  if (stats.hearts > 0) {
    stats.hearts -= 1;
    if (!stats.heartsLastRefill) stats.heartsLastRefill = new Date().toISOString();
    saveStats(stats);
  }
  return stats.hearts;
}

export function refillHearts() {
  const stats = getStoredStats();
  const cost = 50;
  if (stats.gems >= cost) {
    stats.gems -= cost;
    stats.hearts = stats.maxHearts;
    stats.heartsLastRefill = new Date().toISOString();
    saveStats(stats);
    return true;
  }
  return false;
}

// Gems System
export function getGems() {
  return getStoredStats().gems || 0;
}

export function earnGems(amount) {
  const stats = getStoredStats();
  stats.gems = (stats.gems || 0) + amount;
  saveStats(stats);
  return stats.gems;
}

export function spendGems(amount) {
  const stats = getStoredStats();
  if ((stats.gems || 0) >= amount) {
    stats.gems -= amount;
    saveStats(stats);
    return true;
  }
  return false;
}

// Streak Freeze
export function buyStreakFreeze() {
  const stats = getStoredStats();
  const cost = 100;
  if (stats.gems >= cost) {
    stats.gems -= cost;
    stats.streakFreezes = (stats.streakFreezes || 0) + 1;
    saveStats(stats);
    return true;
  }
  return false;
}

export function useStreakFreeze() {
  const stats = getStoredStats();
  if ((stats.streakFreezes || 0) > 0) {
    stats.streakFreezes -= 1;
    saveStats(stats);
    return true;
  }
  return false;
}

export { ACHIEVEMENTS, LEVEL_NAMES };
